import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, Injector, runInInjectionContext, untracked, ViewEncapsulation } from '@angular/core';
import { EntityViewCard, MetadataEntityData } from '@jms/model';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { EntityBoardStore } from '../../state';
import { EntityBoardContext } from '../../context';
import { EntityCardComponent } from '../entity.card';
import { buildFallbackCardConfig } from './board.focus.card';
import { revealFocusedBoardCard, scrollBoardFocusRailToTop } from './board.focus.scroll';
import { boardFocusStyleId, boardFocusBridgeStyles } from './board.focus.theme';

@Component({
  selector: 'board-focus',
  templateUrl: './board.focus.component.html',
  styleUrls: ['./board.focus.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [EntityCardComponent],
})
export class BoardFocusComponent {

  private readonly document = inject(DOCUMENT);
  private readonly hostEl = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly store = inject(EntityBoardStore);
  private readonly injector = inject(Injector);

  /** Tracks page for scroll-to-top on change; null until first observation. */
  private lastObservedPage: number | null = null;

  public constructor() {

    DesignSystem.Mount(this.document, boardFocusStyleId, boardFocusBridgeStyles);
    // The focused-card effect below already scrolls a stepped-to card into view.
    this.destroyRef.onDestroy(this.store.registerVisibleOrder(this.$visibleOrder));

    // Page change with no focused card: snap to top like the table. When a reveal/focus id is set,
    // the focused-card effect below scrolls THAT card — jumping to top first is the wrong card.
    effect(() => {
      const page = this.store.$page();

      if (this.lastObservedPage === null) {
        this.lastObservedPage = page;
        return;
      }
      if (this.lastObservedPage === page) return;
      this.lastObservedPage = page;

      if (untracked(() => this.store.$focusedItemId())) return;
      untracked(() => scrollBoardFocusRailToTop(this.focusRail()));
    });

    // Scroll the focused card into view once it is on the page (create reveal or a click off-screen).
    effect(() => {
      const focusedId = this.store.$focusedItemId();
      const items = this.store.$items();
      if (!focusedId || !items.some(item => item?._id === focusedId)) return;

      this.scrollFocusedCardWhenPainted(focusedId);
    });
  }

  protected readonly $context = computed(() => this.store.$context() as EntityBoardContext);
  protected readonly $items = computed(() => this.store.$items());
  // Keyed by `_id`, as the rail tracks and focuses its cards.
  private readonly $visibleOrder = computed<string[]>(() => this.$items().map(item => item?._id).filter(id => id != null && id !== '').map(id => String(id)));
  protected readonly $metadata = computed(() => this.store.$metadata() as MetadataEntityData | null);
  protected readonly $cardConfig = computed<Partial<EntityViewCard> | null>(() => this.store.$effectiveView()?.card2?.card ?? this.store.$effectiveView()?.card ?? buildFallbackCardConfig(this.store.$cardFields()));

  protected onItemFocused(item: any): void {

    this.store.whenPanelCanLeave(() => this.store.setFocusedItemId(item?._id ?? null));

    const context = this.$context();
    const interaction = context?.itemInteraction;
    if (item && interaction?.navigate) {
      // Injection context so manifest navigate callbacks may `inject()` (e.g. EntityInspectors).
      runInInjectionContext(this.injector, () => interaction.navigate!(item));
    }
  }

  protected isItemFocused(item: any): boolean {

    return item?._id != null && item._id === this.store.$focusedItemId();
  }

  private focusRail(): HTMLElement | null {

    return this.hostEl.nativeElement.querySelector('.board-focus-body');
  }

  /** Cards paint after the items signal; retry a few frames before giving up. */
  private scrollFocusedCardWhenPainted(focusedId: string, attemptsLeft = 8): void {

    const view = this.document.defaultView;
    if (!view) return;

    view.requestAnimationFrame(() => {
      if (revealFocusedBoardCard(this.focusRail(), focusedId)) return;
      if (attemptsLeft <= 0) return;
      this.scrollFocusedCardWhenPainted(focusedId, attemptsLeft - 1);
    });
  }
}
